import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import PersonIcon from "@material-ui/icons/Person";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import EventIcon from "@material-ui/icons/Event";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import LinearProgress from "@material-ui/core/LinearProgress";
import { useForm } from "react-hook-form";
import axios from "axios";

import { showSnackbar } from "../../../../../data-store/actions/organizers-actions";
import { useTracking } from "react-tracking";
import { useLocation } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  root: { flexGrow: 1, width: "100%", backgroundColor: "black" },
  paper: {
    padding: "20px",
    backgroundColor: "#283747",
    textAlign: "left",
    minHeight: "400px",
    overflowY: "scroll",
  },
  buttons: { margin: 30, width: "250px", color: "black" },
}));

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    // si ya existe, no lo vuelvas a cargar
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) return resolve(true);

    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = (e) => reject(e);
    document.body.appendChild(s);
  });
}

function safeDigitsOnly(v = "") {
  return String(v).replace(/[^\d]/g, "");
}

function getLast4(cardNumber) {
  const digits = safeDigitsOnly(cardNumber);
  return digits.slice(-4);
}

function normalizeYearTo2Digits(year) {
  // Openpay suele esperar "26" y no "2026"
  const y = String(year || "").trim();
  if (y.length === 4) return y.slice(2);
  return y;
}

export default function CardPaymentOpenPay(props) {
  let { callback, order, currencyBase, currentOrder, eventDetail } = props;

  const dispatch = useDispatch();
  const classes = useStyles();
  const { t } = useTranslation("inscripcion");

  const registryParticipantKey = useSelector(
    (state) => state.registry.registryParticipantKey
  );
  const eventConfiguration = useSelector(
    (state) => state.events.eventConfiguration
  );

  const { trackEvent } = useTracking();
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();

  const { register, handleSubmit, errors, watch } = useForm();

  const [sending, setSending] = useState(false);
  const [openpayReady, setOpenpayReady] = useState(false);
  const [deviceSessionId, setDeviceSessionId] = useState("");

  // Lee el número de tarjeta en vivo mientras lo teclean
  const cardNumberLive = watch("cardNumber", "") || "";


  // Calcula amount + items como ya lo hacías (reusando tu lógica)
  const computed = useMemo(() => {
    let amount = 0;
    const items = order || [];
    const itemsForBackend = [];

    for (let i in items) {
      let price = items[i].unit_price;
      price =
        Math.round(parseFloat(price * currencyBase.cambioAPeso) * 100) / 100;

      const item = {
        name: items[i].name,
        quantity: items[i].quantity,
        unit_price: Math.round(price * 100), // centavos
      };

      amount =
        Math.round(
          (parseFloat(amount) +
            parseFloat(price) * parseFloat(items[i].quantity)) *
          100
        ) / 100;

      itemsForBackend.push(item);
    }

    return { amount, itemsForBackend };
  }, [order, currencyBase]);
  const test = eventConfiguration.prueba;
  console.log("test", test, eventConfiguration);
  // 🔑 Credenciales Openpay (PUBLIC) para tokenización
  // Recomendado: ponerlas en env:
  const REACT_APP_OPENPAY_MERCHANT_ID = "m69nllrproz6ewnevjde"
  const REACT_APP_OPENPAY_PUBLIC_KEY_TEST = "pk_4556509393e142eda47f58f28cfd2069"
  const REACT_APP_OPENPAY_PUBLIC_KEY = "pk_4f56d6a548844d76b38b1f01dc9ac54a"
  const REACT_APP_OPENPAY_SANDBOX = test;
  const openpayConfig = useMemo(() => {
    const merchantId = REACT_APP_OPENPAY_MERCHANT_ID || "";
    const publicKey = test ? REACT_APP_OPENPAY_PUBLIC_KEY_TEST : REACT_APP_OPENPAY_PUBLIC_KEY;
    // si estás pasando "test" como prop, úsalo
    const sandbox = test ? true : String(REACT_APP_OPENPAY_SANDBOX || "false") === "true";
    return { merchantId, publicKey, sandbox };
  }, [test]);

  const initOpenpay = useCallback(async () => {
    try {
      // ✅ Scripts oficiales (estos suelen ser los usados)
      // Si en tu cuenta Openpay te dan otra URL/version del openpay-data, reemplázala aquí.
      await loadScriptOnce("https://openpay.s3.amazonaws.com/openpay.v1.min.js");
      await loadScriptOnce(
        "https://resources.openpay.mx/lib/openpay-data-js/1.2.38/openpay-data.v1.min.js"
      );

      if (!window.OpenPay) {
        throw new Error("OpenPay no está disponible (script no cargó).");
      }
      if (!openpayConfig.merchantId || !openpayConfig.publicKey) {
        throw new Error("Faltan REACT_APP_OPENPAY_MERCHANT_ID / REACT_APP_OPENPAY_PUBLIC_KEY.");
      }

      window.OpenPay.setId(openpayConfig.merchantId);
      window.OpenPay.setApiKey(openpayConfig.publicKey);
      window.OpenPay.setSandboxMode(!!openpayConfig.sandbox);

      // Device Session (antifraude)
      // setup(formId, hiddenFieldName) -> retorna device_session_id
      const dsid = window.OpenPay.deviceData.setup("openpay-payment-form", "openpayDeviceSessionId");
      setDeviceSessionId(dsid || "");
      setOpenpayReady(true);
    } catch (e) {
      console.error(e);
      setOpenpayReady(false);
      dispatch(showSnackbar(true, t("openpay.errorCargando"), 4000));
    }
  }, [dispatch, openpayConfig]);

  useEffect(() => {
    initOpenpay();
  }, [initOpenpay]);

  function validateBasic(data) {
    // Openpay tokeniza igual aunque validaciones sean básicas,
    // pero aquí evitamos errores obvios antes de llamar a Openpay.
    const cardNumber = safeDigitsOnly(data.cardNumber);
    const month = Number(data.month);
    const year2 = Number(normalizeYearTo2Digits(data.year));
    const cvv = String(data.securityCode || "").trim();

    if (!openpayReady) return t("openpay.noListo");
    if (!deviceSessionId) return t("openpay.sinDeviceSession");
    if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19)
      return t("openpay.revisaNumero");
    if (!month || month < 1 || month > 12) return t("openpay.revisaMes");
    if (!year2 || year2 < 0 || year2 > 99) return t("openpay.revisaAnio");
    if (!cvv || cvv.length < 3 || cvv.length > 4) return t("openpay.revisaCvc");

    return null;
  }

  async function onSubmit(data) {
    setSending(true);

    const errorMsg = validateBasic(data);
    if (errorMsg) {
      dispatch(showSnackbar(true, errorMsg, 4000));
      setSending(false);
      return;
    }

    const cardOwner = data.cardOwner;
    const cardNumber = safeDigitsOnly(data.cardNumber);
    const expirationMonth = String(Number(data.month)).padStart(2, "0");
    const expirationYear = normalizeYearTo2Digits(data.year);
    const cvv2 = String(data.securityCode).trim();

    // Openpay token
    const cardData = {
      card_number: cardNumber,
      holder_name: cardOwner,
      expiration_year: String(expirationYear),
      expiration_month: String(expirationMonth),
      cvv2,


    };

    const successCallback = (token) => {
      try {
        const last4 = getLast4(cardNumber);

        // ✅ URL a donde Openpay regresará después del 3DS
        // Ideal: tu dominio real y una ruta específica que tú controles.
        const eventoIndex = "evento_" + eventDetail.iDEvento;
        const redirectUrlOld = `${window.location.origin}/openpay/return?eventoIndex=${encodeURIComponent(
          eventoIndex
        )}&inscritoKey=${encodeURIComponent(registryParticipantKey)}`;
        const redirectUrl = `${window.location.origin}/${eventoIndex}?event=${encodeURIComponent(
          eventoIndex
        )}&registry=${encodeURIComponent(registryParticipantKey)}&secure3d=true`;

        const jsonDatos = {
          correo: currentOrder.correo,
          tokenId: token.data.id,
          deviceSessionId: deviceSessionId,

          // ✅ 3DS
          use_3d_secure: true,
          redirect_url: redirectUrl,

          nombres: cardOwner,
          telefono: currentOrder.telefono,
          tipoPago: "card",
          inscritoKey: registryParticipantKey,
          eventoIndex,
          last4,
          brand: "",
          amount: computed.amount,
          jsonItems: computed.itemsForBackend,
          orderId: currentOrder?.orderId || currentOrder?.id || undefined,
        };

        const headers = { headers: { "Access-Control-Allow-Origin": true } };

        axios
          .post(
            "https://us-central1-kmetasfirebase.cloudfunctions.net/openpayCargoV1",
            jsonDatos,
            headers
          )
          .then((res) => {
            console.log("Openpay response", res?.data);

            // ✅ Si requiere 3DS: redirigir al banco
            if (res?.data?.requires3ds && res?.data?.redirectUrl) {
              // guarda contexto para el return
              localStorage.setItem("openpay_chargeId", res.data.chargeId || "");
              localStorage.setItem("openpay_eventoIndex", eventoIndex);
              localStorage.setItem("openpay_inscritoKey", registryParticipantKey);

              window.location.href = res.data.redirectUrl;
              return;
            }

            // ✅ Si no requiere 3DS y fue ok: flujo normal
            if (res?.data?.ok) {
              callback(true, false);
              dispatch(showSnackbar(true, t("pago.pagoExitoso"), 4000));

              const tkl = query.get("tkl");
              trackEvent({
                eventName: eventoIndex,
                typeEvent: "Purchase",
                params: {
                  label: "Purchase",
                  tkl: tkl || "direct",
                  order: order,
                  eventName: eventDetail.nombreEvento,
                  facebookPixel: eventDetail.facebookPixel || false,
                  gaId: eventDetail.gaId || false,
                  gateway: "openpay",
                },
              });
            } else {
              dispatch(
                showSnackbar(
                  true,
                  res?.data?.message || t("openpay.pagoNoCompletado"),
                  4000
                )
              );
            }

            setSending(false);
          })
          .catch((error) => {
            console.error("Openpay charge error", error);
            setSending(false);

            // ✅ aquí muestra el mensaje ya “bonito” que definiste en backend
            const backendMsg =
              error?.response?.data?.message ||
              error?.response?.data?.error?.description ||
              error?.message ||
              "Intenta nuevamente.";

            dispatch(
              showSnackbar(
                true,
                t("oxxoPayment.errorProcesandoPago", { detalle: backendMsg }),
                5000
              )
            );
          });
      } catch (e) {
        console.error(e);
        setSending(false);
        dispatch(showSnackbar(true, t("openpay.errorInternoToken"), 4000));
      }
    };

    const errorCallback = (err) => {
      console.log("error tokenizando Openpay", err);
      setSending(false);

      // Openpay suele traer err.description / err.message
      const msg = err?.description || err?.message || t("openpay.errorTokenizando");
      dispatch(showSnackbar(true, `${t("tarjeta.errorPrefijo")} ${msg}`, 4000));
    };

    try {
      window.OpenPay.token.create(cardData, successCallback, errorCallback);
    } catch (e) {
      console.error(e);
      setSending(false);
      dispatch(showSnackbar(true, t("openpay.noSePudoTokenizar"), 4000));
    }
  }

  return (
    <Grid
      container
      direction="row"
      style={{ height: "auto", width: "100%" }}
    >
      <Grid item xs={12} sm={12} style={{ padding: "10px", textAlign: "center" }}>
        <Typography color="primary" component="h1" variant="h3">
          {t("tarjeta.tituloTarjeta")}
        </Typography>
        <Typography color="primary" component="h1" variant="h6">
          {t("tarjeta.ingresaDatos")}
        </Typography>
        {!openpayReady && (
          <Typography color="secondary" variant="body2">
            {t("openpay.cargando")}
          </Typography>
        )}
      </Grid>

      <form
        id="openpay-payment-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: "100%" }}
      >
        {/* hidden para device session (Openpay lo usa/llena con setup) */}
        <input type="hidden" id="openpayDeviceSessionId" name="openpayDeviceSessionId" />

        <Grid item xs={12} sm={12} alignItems={"left"} style={{ margin: "40px" }}>
          <Typography color="primary" variant="h5">
            {t("tarjeta.nombrePropietario")}
          </Typography>
          <TextField
            style={{ width: "100%" }}
            helperText={errors.cardOwner ? t("tarjeta.campoRequerido") : null}
            InputProps={{
              style: { marginBottom: 15 },
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
            defaultValue={currentOrder.nombres + " " + currentOrder.apellidos}
            error={Boolean(errors.cardOwner)}
            fullWidth
            inputRef={register({ required: true })}
            name="cardOwner"
          />

          <Typography color="primary" variant="h5">
            {t("tarjeta.numeroDeTarjeta")}
          </Typography>
          <TextField
            style={{ width: "100%" }}
            helperText={errors.cardNumber ? t("tarjeta.campoRequerido") : null}
            InputProps={{
              style: { marginBottom: 15 },
              startAdornment: (
                <InputAdornment position="start">
                  <CreditCardIcon />
                </InputAdornment>
              ),
            }}
            error={Boolean(errors.cardNumber)}
            fullWidth
            inputRef={register({ required: true, minLength: 10 })}
            name="cardNumber"
          />

          <Typography color="primary" variant="h5">
            {t("tarjeta.fechaDeVencimiento")}
          </Typography>

          <TextField
            label={t("tarjeta.mes")}
            style={{ width: "50%" }}
            helperText={errors.month ? t("tarjeta.campoRequerido") : null}
            InputProps={{
              style: { marginBottom: 15 },
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon />
                </InputAdornment>
              ),
            }}
            defaultValue={1}
            error={Boolean(errors.month)}
            fullWidth
            inputRef={register({ required: true, min: 1, max: 12 })}
            name="month"
            type="number"
          />

          <TextField
            label={t("tarjeta.anio")}
            style={{ width: "50%" }}
            helperText={errors.year ? t("tarjeta.campoRequerido") : null}
            InputProps={{
              style: { marginBottom: 15 },
              startAdornment: (
                <InputAdornment position="start">
                  <EventIcon />
                </InputAdornment>
              ),
            }}
            defaultValue={new Date().getFullYear()}
            error={Boolean(errors.year)}
            fullWidth
            inputRef={register({ required: true, min: 2020, max: 2050 })}
            name="year"
            type="number"
          />

          <Typography color="primary" variant="h5">
            {t("tarjeta.codigoDeSeguridad")}
          </Typography>

          <TextField
            label="CVC"
            style={{ width: "100%" }}
            helperText={errors.securityCode ? t("tarjeta.campoRequerido") : null}
            InputProps={{
              style: { marginBottom: 15 },
              startAdornment: (
                <InputAdornment position="start">
                  <VerifiedUserIcon color="secondary" />
                </InputAdornment>
              ),
            }}
            defaultValue={""}
            error={Boolean(errors.securityCode)}
            fullWidth
            inputRef={register({ required: true, min: 0, max: 9999 })}
            name="securityCode"
            type="number"
          />

          {sending && (
            <Grid item xs={12} sm={12}>
              <LinearProgress />
            </Grid>
          )}

          {!sending && (
            <Button
              type="submit"
              style={{ width: "100%" }}
              color="primary"
              variant="contained"
              disabled={!openpayReady}
            >
              {t("tarjeta.realizarElPago")}
            </Button>
          )}
          <Grid item xs={12} sm={12} style={{ margin: "20px" }}>

            <Grid
              container
              direction="row"
              style={{ height: "auto", width: "100%" }}
            >
              <Grid item xs={3} sm={3} style={{ padding: "10px", textAlign: "center" }}>
              </Grid>
              <Grid item xs={6} sm={6} >
                {/* <Typography color="secondary" variant="body2">
                  Pasarela de pagos
                </Typography> */}

                <img
                  src="https://firebasestorage.googleapis.com/v0/b/kmetasfirebase.appspot.com/o/Openpay%2FLogotipoOpenpay-01.jpg?alt=media&token=906e9028-fc12-4d35-a10d-3298fd1fb71a"
                  alt="Openpay"
                  height={60}
                />

              </Grid>
              <Grid item xs={3} sm={3} style={{ padding: "10px", textAlign: "center" }}>
              </Grid>

              {/* <Grid item xs={12} sm={12} >
                <Typography color="secondary" variant="body2">
                  Tarjetas aceptadas
                </Typography>
              </Grid> */}


              <Grid item xs={4} sm={4} style={{ padding: "10px", textAlign: "center" }}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/kmetasfirebase.appspot.com/o/Openpay%2Fvisa.png?alt=media&token=806a5a77-11c5-4dab-a72b-28c6ecc035ea"
                  alt="visa"
                  height={40}
                />
              </Grid>
              <Grid item xs={4} sm={4} style={{ padding: "10px", textAlign: "center" }}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/kmetasfirebase.appspot.com/o/Openpay%2FmasterCard.png?alt=media&token=19808d60-aebf-4fd9-816b-dd8e15a8b3a7"
                  alt="Masster Card"
                  height={40}
                />
              </Grid>
              <Grid item xs={4} sm={4} style={{ padding: "10px", textAlign: "center" }}>
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/kmetasfirebase.appspot.com/o/Openpay%2FamericanExpress.png?alt=media&token=cd3b18bc-6514-4e4a-8b7b-3bddf9a28542"
                  alt="American Express"
                  height={40}
                />
              </Grid>
            </Grid>

          </Grid>

          <Grid item xs={12} sm={12} style={{ margin: "20px" }}>
            <Typography color="secondary" variant="body2">
              {t("tarjeta.notaConfirmacion")}
            </Typography>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
}
